import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import {
  countExpansionSubsegments,
  isExpansionParentShellRow,
  parseFootageNumber,
} from "./expansionGroupUtils";
import {
  EXPANSION_SEGMENT_DETAIL_EXPORT_HEADERS,
  formatAwardStatusFromPct,
  normalizeExpansionSegmentFinancials,
  parseFinancialNumber,
} from "./expansionSegmentFinancialsSchema";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import { listContractorsForRole } from "./contractsDerivedFields";
import { serializeCsv } from "./csvParse";

function readConstructionFromRow(row) {
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return {};
  return (
    j[COMMERCIAL_DOMAINS.CONSTRUCTION] ??
    j.construction_details ??
    j["Construction Details"] ??
    {}
  );
}

function expansionSubsegmentRows(children) {
  return (Array.isArray(children) ? children : []).filter((row) => !isExpansionParentShellRow(row));
}

function sumFootageTarget(sub) {
  const override = parseFootageNumber(sub?.override_footage);
  if (override != null && override > 0) return override;
  return parseFootageNumber(sub?.construction_footage);
}

function uniqueSortedLabels(values) {
  return [...new Set(values.map((v) => String(v || "").trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );
}

function contractFieldsForRow(row, contractFieldsByProjectId) {
  const fromApi = contractFieldsByProjectId?.[row?.id]?.fields;
  if (fromApi && typeof fromApi === "object") {
    return { ...readConstructionFromRow(row), ...fromApi };
  }
  return readConstructionFromRow(row);
}

export function computeSubsegmentAwardRollups(children, contractFieldsByProjectId = null) {
  let awardedFootage = 0;
  let projectLevelCostSum = 0;
  let acfOnlyCostSum = 0;
  let hasAward = false;
  /** @type {Map<string, number>} */
  const costByContractId = new Map();

  for (const row of expansionSubsegmentRows(children)) {
    const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
    const cons = contractFieldsForRow(row, contractFieldsByProjectId);
    const footage = sumFootageTarget(sub) ?? 0;
    const cost = parseFinancialNumber(cons.contract_cost) ?? 0;
    const construction = String(cons.construction_contractor ?? cons.contractor ?? "").trim();
    const fiber = String(cons.fiber_contractor ?? "").trim();
    const hasContract =
      cost > 0 ||
      (construction !== "" && construction !== "Pending Award" && construction !== "Cancelled") ||
      fiber !== "";
    if (hasContract) {
      hasAward = true;
      const links = contractFieldsByProjectId?.[row?.id]?.contracts;
      const constructionLinks = Array.isArray(links)
        ? links.filter((c) => c?.appliesConstruction !== false && c?.status !== "cancelled")
        : [];
      const coverageValues = constructionLinks
        .map((c) => {
          const n = Number(c?.coveredFootage);
          return Number.isFinite(n) && n > 0 ? n : null;
        })
        .filter((n) => n != null);
      if (
        constructionLinks.length > 0 &&
        coverageValues.length === constructionLinks.length
      ) {
        const coveredSum = coverageValues.reduce((sum, n) => sum + n, 0);
        awardedFootage += footage > 0 ? Math.min(coveredSum, footage) : coveredSum;
      } else {
        awardedFootage += footage;
      }

      // Prefer unique-by-contract cost when link payloads include effective/base cost.
      // Otherwise fall back to summing each subsegment's project-level contract_cost.
      projectLevelCostSum += cost;
      if (constructionLinks.length > 0) {
        for (const c of constructionLinks) {
          if (c?.id == null) continue;
          const key = String(c.id);
          if (costByContractId.has(key)) continue;
          const n = Number(c?.effectiveContractCost ?? c?.contractCost);
          if (!Number.isFinite(n)) continue;
          costByContractId.set(key, n);
        }
      } else {
        acfOnlyCostSum += cost;
      }
    }
  }

  let awardedContractorCost = null;
  if (hasAward) {
    if (costByContractId.size > 0) {
      let uniqueSum = 0;
      for (const n of costByContractId.values()) uniqueSum += n;
      awardedContractorCost = uniqueSum + acfOnlyCostSum;
    } else {
      awardedContractorCost = projectLevelCostSum;
    }
  }

  return {
    awardedFootage: hasAward ? awardedFootage : null,
    awardedContractorCost,
  };
}

/** Total / existing / new feet rolled up from subsegment summary fields. */
export function computeSegmentFootageRollups(children) {
  let totalFeet = 0;
  let existingFeet = 0;
  let newFeet = 0;
  let hasTotal = false;
  let hasExisting = false;
  let hasNew = false;

  for (const row of expansionSubsegmentRows(children)) {
    const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
    const override = parseFootageNumber(sub?.override_footage);
    const construction = parseFootageNumber(sub?.construction_footage);
    if (override != null) {
      existingFeet += override;
      hasExisting = true;
    }
    if (construction != null) {
      newFeet += construction;
      hasNew = true;
    }
    if (override != null || construction != null) {
      totalFeet += (override ?? 0) + (construction ?? 0);
      hasTotal = true;
    }
  }

  return {
    totalFeet: hasTotal ? totalFeet : null,
    existingFeet: hasExisting ? existingFeet : null,
    newFeet: hasNew ? newFeet : null,
  };
}

export function computeSegmentFootageTotals(children) {
  return computeSegmentFootageRollups(children).totalFeet;
}

export function computeSegmentContractorRollup(children, contractFieldsByProjectId = null) {
  const names = [];
  for (const row of expansionSubsegmentRows(children)) {
    const summary = contractFieldsByProjectId?.[row?.id];
    if (summary) {
      for (const name of listContractorsForRole(summary, "construction")) {
        if (name && name !== "Pending Award" && name !== "Cancelled") names.push(name);
      }
      for (const name of listContractorsForRole(summary, "fiber")) {
        if (name && name !== "Pending Award" && name !== "Cancelled") names.push(name);
      }
      continue;
    }
    const cons = contractFieldsForRow(row, contractFieldsByProjectId);
    const construction = String(cons.construction_contractor || cons.contractor || "").trim();
    const fiber = String(cons.fiber_contractor || "").trim();
    if (construction && construction !== "Pending Award" && construction !== "Cancelled") {
      names.push(construction);
    }
    if (fiber && fiber !== construction) names.push(fiber);
  }
  const unique = uniqueSortedLabels(names);
  return unique.length ? unique.join(", ") : null;
}

export function computeSegmentContractIdsRollup(children, contractFieldsByProjectId = null) {
  const ids = [];
  for (const row of expansionSubsegmentRows(children)) {
    const summary = contractFieldsByProjectId?.[row?.id];
    const contracts = summary?.contracts;
    if (Array.isArray(contracts) && contracts.length) {
      for (const contract of contracts) {
        const label = contract?.contractNumber ?? (contract?.id != null ? String(contract.id) : "");
        if (label) ids.push(String(label).trim());
      }
      continue;
    }
    const fromFields = summary?.fields?._contractNumber;
    if (fromFields) ids.push(String(fromFields).trim());
  }
  const unique = uniqueSortedLabels(ids);
  return unique.length ? unique.join(" ") : null;
}

function roundMoney(n) {
  if (n == null || !Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

function roundPct(n) {
  if (n == null || !Number.isFinite(n)) return null;
  return Math.round(n * 10000) / 10000;
}

function normalizeSegNumberFromLabel(segmentLabel) {
  if (segmentLabel == null || String(segmentLabel).trim() === "") return null;
  const s = String(segmentLabel).trim();
  const m = s.match(/^(\d+)/);
  if (m) return Number(m[1]);
  const all = s.match(/\d+/);
  return all ? Number(all[0]) : null;
}

/** One Segment Detail row — stored inputs + live rollups + formula columns. */
export function buildExpansionSegmentDetailRow({
  woBase,
  segmentLabel,
  children,
  financialsRaw,
  contractFieldsByProjectId = null,
}) {
  const stored = normalizeExpansionSegmentFinancials(financialsRaw);
  const rollups = computeSubsegmentAwardRollups(children, contractFieldsByProjectId);
  const footageRollups = computeSegmentFootageRollups(children);
  const contractorsRollup = computeSegmentContractorRollup(children, contractFieldsByProjectId);
  const contractIdsRollup = computeSegmentContractIdsRollup(children, contractFieldsByProjectId);

  const segNumber = stored.seg_number ?? normalizeSegNumberFromLabel(segmentLabel);
  const subsegmentCount = countExpansionSubsegments(children);
  const totalFeet = footageRollups.totalFeet;
  const existingFeet = footageRollups.existingFeet;
  const newFeet = footageRollups.newFeet;
  const budgetPerFt = stored.budget_per_ft;
  const totalBudget =
    stored.total_budget ??
    (totalFeet != null && budgetPerFt != null ? roundMoney(totalFeet * budgetPerFt) : null);

  const awardedFootage = rollups.awardedFootage;
  const awardedContractorCost = rollups.awardedContractorCost;
  const actualContractorPerFt =
    awardedFootage != null && awardedFootage > 0 && awardedContractorCost != null
      ? roundMoney(awardedContractorCost / awardedFootage)
      : null;

  const materialPerFt = stored.material_per_ft;
  const materialCost =
    totalFeet != null && materialPerFt != null ? roundMoney(totalFeet * materialPerFt) : null;

  const awardedActualInclMaterials =
    awardedContractorCost != null || materialCost != null
      ? roundMoney((awardedContractorCost ?? 0) + (materialCost ?? 0))
      : null;

  const awardedBudgetValue =
    awardedFootage != null && budgetPerFt != null
      ? roundMoney(awardedFootage * budgetPerFt)
      : null;

  const awardedVariance =
    awardedBudgetValue != null && awardedContractorCost != null
      ? roundMoney(awardedBudgetValue - awardedContractorCost)
      : null;

  const remainingFeetToAward =
    totalFeet != null && awardedFootage != null ? Math.max(0, totalFeet - awardedFootage) : null;

  const remainingBudgetToAward =
    totalBudget != null && awardedBudgetValue != null
      ? roundMoney(Math.max(0, totalBudget - awardedBudgetValue))
      : null;

  const remainingBudgetPerFt =
    remainingBudgetToAward != null &&
    remainingFeetToAward != null &&
    remainingFeetToAward > 0
      ? roundMoney(remainingBudgetToAward / remainingFeetToAward)
      : null;

  const environmental = stored.environmental_allocation;
  const permitting = stored.permitting_allocation;
  const overblow = stored.overblow_cost ?? 0;

  const expectedFinalCost =
    awardedActualInclMaterials != null
      ? roundMoney(
          awardedActualInclMaterials + (environmental ?? 0) + (permitting ?? 0) + overblow
        )
      : null;

  const finalVarianceVsBudget =
    totalBudget != null && expectedFinalCost != null
      ? roundMoney(totalBudget - expectedFinalCost)
      : null;

  const pctAwarded =
    totalFeet != null && totalFeet > 0 && awardedFootage != null
      ? roundPct(awardedFootage / totalFeet)
      : null;

  return {
    woBase,
    "Seg#": segNumber,
    Subsegments: subsegmentCount,
    "Total Feet": totalFeet,
    "Existing Feet": existingFeet,
    "New Feet": newFeet,
    Owner: stored.owner,
    "Overblow Cost": stored.overblow_cost,
    "Budget $/Ft": budgetPerFt,
    "Total Budget": totalBudget,
    "Contractor(s)": contractorsRollup,
    "Contract ID(s)": contractIdsRollup,
    "Awarded Footage": awardedFootage,
    "Awarded Contractor Cost": awardedContractorCost,
    "Actual Contractor $/Ft": actualContractorPerFt,
    "Material $/Ft": materialPerFt,
    "Material Cost": materialCost,
    "Awarded Actual Incl. Materials": awardedActualInclMaterials,
    "Awarded Budget Value": awardedBudgetValue,
    "Awarded Variance": awardedVariance,
    "Remaining Feet to Award": remainingFeetToAward,
    "Remaining Budget to Award": remainingBudgetToAward,
    "Remaining Budget $/Ft": remainingBudgetPerFt,
    "Environmental Allocation": environmental,
    "Permitting Allocation": permitting,
    "Expected Final Cost": expectedFinalCost,
    "Final Variance vs Budget": finalVarianceVsBudget,
    "% Awarded": pctAwarded,
    Status: formatAwardStatusFromPct(pctAwarded),
  };
}

export function buildExpansionSegmentDetailExportRows(segmentGroups, financialsMap = {}, contractFieldsByProjectId = null) {
  const map = financialsMap && typeof financialsMap === "object" ? financialsMap : {};
  return segmentGroups.map((group) =>
    buildExpansionSegmentDetailRow({
      woBase: group.woBase || group.key,
      segmentLabel: group.segment,
      children: group.children || [],
      financialsRaw: map[group.woBase || group.key] || map[group.key],
      contractFieldsByProjectId,
    })
  );
}

export function expansionSegmentDetailRowsToCsv(rows) {
  const data = rows.map((row) => {
    const out = {};
    for (const h of EXPANSION_SEGMENT_DETAIL_EXPORT_HEADERS) {
      out[h] = row[h] ?? "";
    }
    return out;
  });
  return serializeCsv(EXPANSION_SEGMENT_DETAIL_EXPORT_HEADERS, data);
}

export function downloadExpansionSegmentDetailCsv(rows, filename = "expansion-segment-detail.csv") {
  const csv = "\uFEFF" + expansionSegmentDetailRowsToCsv(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
